import { supabase } from './serviceService.js';

// CTA copy lives in a single row in Supabase.
const TABLE = 'cta_content';
const CTA_CACHE_KEY = 'rb_cta_cache_v1';
const CTA_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;
export const DEFAULT_CTA_CONTENT = {
  id: 1,
  headline1: 'Osäker vad du vill ha hjälp med?',
  headline2: 'BOKA EN KOSTNADSFRI KONSULTATION MED OSS.',
  body: 'Vi ser framemot långsiktiga affärspartnerskap och kommer göra vårt bästa för att förverkliga din ekonomiska vision. Boka första mötet redan idag.',
  buttonText: 'Boka konsultation',
  buttonHref: '#kontakt',
};
let ctaCache = null;
let ctaInFlightPromise = null;

const canUseStorage = () => typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

const readCtaStorageCache = () => {
  if (!canUseStorage()) return null;
  try {
    const raw = window.localStorage.getItem(CTA_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if ('headline1' in parsed || 'buttonText' in parsed) {
      return { content: parsed, isFresh: true };
    }
    const content = parsed.content && typeof parsed.content === 'object' ? parsed.content : null;
    const savedAt = Number(parsed.savedAt || 0);
    if (!content) return null;
    const isFresh = savedAt > 0 ? Date.now() - savedAt < CTA_CACHE_MAX_AGE_MS : true;
    return { content, isFresh };
  } catch (_) {
    return null;
  }
};

const writeCtaStorageCache = (content) => {
  if (!canUseStorage() || !content || typeof content !== 'object') return;
  try {
    window.localStorage.setItem(CTA_CACHE_KEY, JSON.stringify({
      savedAt: Date.now(),
      content,
    }));
  } catch (_) {
  }
};

const getCtaFallback = () => readCtaStorageCache()?.content || DEFAULT_CTA_CONTENT;

const mapDbToModel = (row) => ({
  id: row.id,
  headline1: row.headline1 ?? row.headline_1 ?? '',
  headline2: row.headline2 ?? row.headline_2 ?? '',
  body: row.body ?? '',
  buttonText: row.button_text ?? row.buttonText ?? '',
  buttonHref: row.button_href ?? row.buttonHref ?? '#',
});

const mapModelToDb = (data) => ({
  headline1: data.headline1 ?? data.headline_1 ?? null,
  headline2: data.headline2 ?? data.headline_2 ?? null,
  body: data.body ?? null,
  button_text: data.buttonText ?? data.button_text ?? null,
  button_href: data.buttonHref ?? data.button_href ?? null,
});

export const ctaService = {
  get: async ({ force = false } = {}) => {
    if (!force && ctaCache) {
      return ctaCache;
    }

    if (!force) {
      const storageCache = readCtaStorageCache();
      if (storageCache?.content && storageCache.isFresh) {
        ctaCache = storageCache.content;
        return storageCache.content;
      }
    }

    if (!force && ctaInFlightPromise) {
      return ctaInFlightPromise;
    }

    ctaInFlightPromise = supabase
      .from(TABLE)
      .select('*')
      .eq('id', 1)
      .maybeSingle()
      .then(({ data, error }) => {
        if (error) throw error;
        const resolved = data ? mapDbToModel(data) : getCtaFallback();
        ctaCache = resolved;
        writeCtaStorageCache(resolved);
        return resolved;
      })
      .catch((error) => {
        const fallbackContent = getCtaFallback();
        ctaCache = fallbackContent;
        if (fallbackContent) {
          return fallbackContent;
        }
        throw error;
      })
      .finally(() => {
        ctaInFlightPromise = null;
      });

    return ctaInFlightPromise;
  },

  upsert: async (data) => {
    const payload = { id: 1, ...mapModelToDb(data) };
    const { data: result, error } = await supabase
      .from(TABLE)
      .upsert(payload, { onConflict: 'id' })
      .select()
      .single();
    if (error) throw error;
    const mapped = mapDbToModel(result);
    ctaCache = mapped;
    writeCtaStorageCache(mapped);
    return mapped;
  },
};
